import { ApplicantDetails, Application, IApplicantOffer } from '../../apply/index.js';
import { EllucianAuthMethod, IEllucian } from '../../config/index.js';
import { Payload, Method } from '../../generic-types/index.js';
import { z } from 'zod';

export enum EllucianHeaderMediaTypes {
  JSON = 'application/json',
  V6 = 'application/vnd.hedtech.integration.v6+json',
  V7 = 'application/vnd.hedtech.integration.v7+json',
  V15 = 'application/vnd.hedtech.integration.v15+json',
  V16 = 'application/vnd.hedtech.integration.v16+json',
  V16_1_0 = 'application/vnd.hedtech.integration.v16.1.0+json',
  V1_0_0 = 'application/vnd.hedtech.integration.v1.0.0+json',
  V1 = 'application/vnd.hedtech.integration.v1+json',
  V11 = 'application/vnd.hedtech.integration.v11+json',
  V12 = 'application/vnd.hedtech.integration.v12+json',
  V1_Admission_Application = 'application/vnd.hedtech.integration.admission-applications-submissions.v1+json',
}

export enum EllucianEndPoint {
  PERSON_FIND_OR_CREATE = 'person-find-or-create-requests',
  ETHNICITIES = 'ethnicities',
  CITIZENSHIP_STATUSES = 'citizenship-statuses',
  ADMISSION_APPLICATION = 'admission-applications',
  ADMISSION_POPULATION = 'admission-populations',
  RESIDENCY_TYPES = 'residency-types',
  SITES = 'sites',
  ACADEMIC_PROGRAMS = 'x-uog-adtx-programme-courses',
  COUNTRIES = 'countries',
  ACADEMIC_PERIODS = 'academic-periods',
  ADMISSION_APPLICATION_ERRORS = 'x-uog-adtx-errors',
  PERSON_RECORD = 'persons',
  MEDICAL_CODES = 'medical-codes',
  DISABILITY_TYPES = 'disability-types',
  ADD_MEDICAL_DETAILS = 'medical-details/batches',
  GET_ASSESSMENT_IDS = 'aptitude-assessments',
  CREATE_GRADES = 'student-aptitude-assessments',
  GET_TADM_CODE = 'test-score-administration-type-codes',
  ACCOMMODATION_PORTAL_URL = 'x-uog-accommodation-portal',
}

export enum EllucianCitizenCategory {
  CITIZEN = 'citizen',
  NON_CITIZEN = 'nonCitizen',
}

export enum EllucianMatchingStatus {
  NEW = 'NEW',
  IN_PROGRESS = 'IN_PROGRESS',
  VALIDATION_DONE = 'VALIDATION_DONE',
  MATCHING = 'MATCHING',
  MATCHING_DONE = 'MATCHING_DONE',
  VERIFYING = 'VERIFYING',
  VERIFIED = 'VERIFIED',
  COMPLETED = 'COMPLETED',
}

export enum EllucianAdmissionPopulation {
  UNDECLARED = '0',
  NEW = 'N',
  CONTINUING = 'C',
  DIRECT_ENTRY = 'D',
  REPEATER = 'R',
  CONT_INTERMEDIATE_STANDING = 'I',
}

export enum EllucianResidencyType {
  UNDECLARED = '0',
  HOME_ASSESSED = 'H',
  NOT_ASSESSED_HOME = 'N',
  OVERSEAS_ASSESSED = 'O',
  UNKNOWN_RESIDENCY_DEFAULT = 'U',
  NOT_ASSESSED_OVERSEAS = 'X',
}

export interface IGenericEllucianType {
  code: string;
  title: string;
  id: string;
}

export type ISiteIDS = IGenericEllucianType;
export type IAdmissionPopulationTypeGUIDs = IGenericEllucianType;
export type AptitudeAssessmentsTypeGUIDs = IGenericEllucianType;
export type AdmissionDecisionTypeGUIDs = IGenericEllucianType;
export type EthnicitiesTypeGUIDs = IGenericEllucianType;
export type CitizenshipStatusTypeGUIDs = IGenericEllucianType & { category: EllucianCitizenCategory };
export type ResidencyTypeGUIDs = IGenericEllucianType;

export interface AcademicPeriodGUIDs {
  code: string;
  id: string;
  institution: Institution;
  startOn: Date;
  title: string;
  xProcYear: string;
}

export interface Institution {
  id: string;
}

export type EllucianMedicalCodes = {
  activityDate: string;
  code: string;
  desc: string;
};

export type AssessmentTypeTADMCodes = {
  activityDate: string;
  code: string;
  desc: string;
};

export interface Ent {
  id: string;
}

export interface Score {
  type: string;
  value: string;
}

export interface UogExtensions {
  tadmCode: string;
}

export interface Metadata {
  createdBy: string;
  createdOn: string;
  modifiedBy: string;
  modifiedOn: string;
}

export interface StudentAptitudeAssessmentRecords {
  id: string;
  student: Ent;
  assessment: Ent;
  assessedOn: string;
  score: Score;
  uogExtensions: UogExtensions;
  status: string;
}

/** `Payload` for Student Assessment Records `POST` api */
export interface StudentAptitudeAssessmentRecordsPayload extends StudentAptitudeAssessmentRecords {
  metadata: Metadata;
}

export type PersonMatchingResponse = PersonManager & {
  status: EllucianMatchingStatus.COMPLETED;
  personId: string;
  matchResultType: string;
};

export interface PersonManager {
  id: string;
  source: string;
  status: EllucianMatchingStatus;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
  skipCreate: boolean;
  skipUpdate: boolean;
  confidential: boolean;
  skipVerification: boolean;
  otherInterestedSources: string[];
  matchResult: any[];
}

export interface MatchResult {
  time: string;
  matches: Matches;
  ruleSet: string;
  ruleSetId: string;
  ruleSetVersion: number;
  potentialMatches: Matches;
}

export interface Matches {
  persons: any[];
  'constituent-persons': any[];
  'recruitment-persons': any[];
}

export interface MedicalDataUploadResponse {
  items: any[];
  resource: { name: string; version: string };
  status: string;
}

export interface IAccommodationPortalResponse {
  accommodationPortalURL: string;
}

export interface UogListOfActiveProgram {
  id: string;
  code: string;
  applicationAcademicProgrammeGuid: string;
  disciplineGuid: string;
  shortDescription: string;
  description: string;
  awardCode: string;
  awardDescription: string;
  awardTitle: string;
  levelCode: string;
  levelDescription: string;
  subject1: string;
  subject1Description: string;
  courseLength: string;
  courseLengthDescription: string;
  termCode: string;
  site: string;
  siteName: string;
  facultyCode: string;
  facultyDescription: string;
  entryPoint: string;
  programmeMode: string;
  programmeModeDescription: string;
  startMonth: string;
  recruiting: string;
  apprenticeship: string;
}

export interface AdmissionDecision {
  application: {
    id: string;
  };
  decidedOn: string;
  decisionType: {
    id: string;
  };
  id: string;
}

export interface AdmissionDecisionPayload extends AdmissionDecision {
  metadata: Metadata;
}

export type EllucianApiConfig = Pick<
  IEllucian,
  | 'ellucianUrl'
  | 'ellucianUrlBasePath'
  | 'ellucianEnabled'
  | 'ellucianToken'
  | 'ellucianValueMappings'
  | 'ellucianFeatures'
>;

export interface IEllucianOrchestrationParams {
  uni: string;
  userId: string;
  applicant: ApplicantDetails | Application;
  offer: IApplicantOffer;
  ellucianConfig: EllucianApiConfig;
}

interface LegalEllucianName {
  type: { category: string };
  fullName: string;
}

interface EllucianName {
  firstName: string;
  middleName?: string;
  lastName: string;
  prefix: string;
}

interface EllucianIdentityDocument {
  type: {
    category: string;
  };
  documentId: string;
  countryCode: string;
  endOn: string;
}

export interface IEllucianPerson {
  skipCreate: boolean;
  skipVerification: boolean;
  skipUpdate: boolean;
  source: string;
  confidential: boolean;
  otherInterestedSources: string[];
  persons: {
    birthCountry: string;
    dateOfBirth: string;
    names: Array<LegalEllucianName | EllucianName>;
    ethnicities: Array<{
      ethnicGroup: {
        id: string;
      };
    }>;
    gender: {
      value: string;
    };
    citizenship: Array<{
      status: string;
      country: string;
      statusDetail: {
        id: string;
      };
    }>;
    emails: Array<{
      preference?: string;
      address: string;
      type: {
        category: string;
      };
    }>;
    phones: Array<{
      preference?: string; // Optional as it's not present in the second phone object
      number: string;
      extension?: string; // Optional as it's commented out
      type: {
        category: string;
      };
      countryCallingCode: string;
    }>;
    addresses: Array<{
      addressLines: string[];
      countryCode: string;
      locality: string;
      postalCode: string;
      type: {
        category: string;
      };
    }>;
    identityDocuments?: Array<EllucianIdentityDocument>;
  };
}

export interface IEllucianApplicationData {
  id: string;
  type: {
    id: string;
  };
  academicPeriod: {
    id: string;
  };
  applicant: {
    id: string;
  };
  admissionPopulation: {
    id: string;
  };
  residencyType: {
    id: string;
  };
  site: {
    id: string;
  };
  applicationAcademicPrograms: Array<{
    program: {
      id: string;
    };
    disciplines: Array<{
      discipline: {
        id: string;
      };
    }>;
  }>;
  uogExtensions: {
    applicationCurriculum: {
      siteCode: string;
      mode: string;
      entry: string;
    };
    ucasPerson: {
      care: string;
      parEd: string;
      fPay: string;
      domicile: string;
      entryMonth: string;
      entryYear: string;
      resCat: string;
      ukEntryDate: string;
      appLink: string;
    };
  };
}

export interface IEllucianMedicalItem {
  bannerId: string;
  medicalCondition: string;
  primaryDisability: boolean;
  disabilityType: string;
  comment?: string;
}
export interface IEllucianMedicalData {
  id: '00000000-0000-0000-0000-000000000000';
  items: IEllucianMedicalItem[];
}

export interface IEllucianAptitudeAssessment {
  id: '00000000-0000-0000-0000-000000000000';
  assessment: {
    id: string;
  };
  student: {
    id: string;
  };
  assessedOn: string;
  score: {
    type: 'literal' | 'numeric';
    value: string;
  };
  uogExtensions: {
    tadmCode: string;
    teacCode?: string;
  };
  status: 'active' | 'inactive';
}

export type EllucianRequest = {
  ellucianConfig: Pick<IEllucian, 'ellucianUrl' | 'ellucianUrlBasePath' | 'ellucianEnabled' | 'ellucianToken'>;
  userId?: string;
  url: string;
  personId?: string;
  uni: string;
  applicationId?: string;
  method: Method;
  headers?: Record<string, string>;
} & Payload;

export const EllucianIntegrationSchema = z
  .object({
    ellucianEnabled: z.boolean(),
    ellucianAuthMethod: z.enum(Object.values(EllucianAuthMethod) as [string, ...string[]]),
    ellucianUrl: z.string().url({ message: 'Invalid URL' }).optional(),
    ellucianUrlBasePath: z.string().optional(),
    ellucianUserName: z.string().optional(),
    ellucianPassword: z.string().optional(),
    ellucianClientId: z.string().optional(),
    ellucianSecret: z.string().optional(),
    ellucianToken: z.string().optional(),
    ellucianOAuthScope: z.string().optional(),
    ellucianValueMappings: z
      .array(z.object({ fieldType: z.string(), values: z.array(z.object({ source: z.string(), target: z.string() })) }))
      .optional(),
    ellucianFeatures: z
      .record(
        z.object({
          label: z.string(),
          enable: z.boolean(),
        }),
      )
      .default({}),
  })
  .superRefine((data, ctx) => {
    if (!data.ellucianEnabled) {
      return;
    }

    if (data.ellucianEnabled) {
      if (!data.ellucianUrl) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ellucianUrl'],
          message: 'Ellucian Base URL is required',
        });
      }
      if (!data.ellucianUrlBasePath) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ellucianUrlBasePath'],
          message: 'Ellucian URL Base Path is required',
        });
      }
    }

    if (data.ellucianAuthMethod === EllucianAuthMethod.Basic) {
      if (!data.ellucianUserName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ellucianUserName'],
          message: 'Username is required for Basic authentication',
        });
      }
      if (!data.ellucianPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ellucianPassword'],
          message: 'Password is required for Basic authentication',
        });
      }
    }
    if (data.ellucianAuthMethod === EllucianAuthMethod.OAuth) {
      if (!data.ellucianClientId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ellucianClientId'],
          message: 'Client ID is required for OAuth authentication',
        });
      }
      if (!data.ellucianSecret) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ellucianSecret'],
          message: 'Secret is required for OAuth authentication',
        });
      }
      if (!data.ellucianOAuthScope) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ellucianOAuthScope'],
          message: 'OAuth Scope is required for OAuth authentication',
        });
      }
    }
    if (data.ellucianAuthMethod === EllucianAuthMethod.SecretToken) {
      if (!data.ellucianToken) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ellucianToken'],
          message: 'Ellucian Secret Token is required for authentication',
        });
      }
    }
  });

export type EllucianFormValues = z.infer<typeof EllucianIntegrationSchema>;

export enum ReferenceDataType {
  SITE = 'SITE',
  ETHNICITY = 'ETHNICITY',
  CITIZENSHIP_STATUS = 'CITIZENSHIP_STATUS',
  ACADEMIC_PERIOD = 'ACADEMIC_PERIOD',
  ADMISSION_POPULATION = 'ADMISSION_POPULATION',
  RESIDENCY_TYPE = 'RESIDENCY_TYPE',
  ACADEMIC_PROGRAM = 'ACADEMIC_PROGRAM',
  DISABILITY_TYPE = 'DISABILITY_TYPE',
  APTITUDE_ASSESSMENT = 'APTITUDE_ASSESSMENT',
  MEDICAL_CODES = 'MEDICAL_CODES',
}

export enum EllucianCare {
  YES = '1',
  NO = '2',
  UNANSWERED = '7',
}

export enum EllucianParentsEd {
  YES = '1',
  NO = '2',
  'No Response Given' = '7',
  "Don't Know" = '8',
  'Information Refused' = '9',
}

export enum EllucianMedicalDesc {
  NO_DISABILITY = 'No disability',
  DISABILITY_NOT_LISTED = 'Disability not listed',
}

export enum EllucianDisabilityTitle {
  NO_KNOWN_DISABILITY = 'No known disability',
  DISABLED_NO_DSA = 'Disabled - no DSA',
  DISABLED_DSA_STATUS_UNKNOWN = 'Disabled - DSA status unknown',
}

export interface EllucianMedicalRecordUpdateResponse {
  id: string; // Batch Id
  jobId: number;
  resource: {
    name: string;
    version: string;
  };
  status: 'inProgress' | 'completed';
}

export interface EllucianMedicalDetailsResponse {
  endTime: string;
  id: string;
  items: EllucianMedicalDetailItem[];
  jobId: number;
  lastModified: string;
  lastModifiedBy: string;
  resource: {
    name: string;
    version: string;
  };
  startDate: string;
  startTime: string;
  status: 'inProgress' | 'completed';
  totalErrorsCount: number;
}

export interface EllucianMedicalDetailItem {
  errorMessage?: string;
  id?: {
    bannerId: string;
    medicalCondition: string;
  };
}

export interface EllucianDataMedicalDetails {
  apiCalled: boolean;
  data: {
    totalErrorsCount: number;
    status: 'inProgress' | 'completed';
    items: Array<EllucianMedicalDetailItem>;
  };
}

export interface EllucianGradesRecord {
  apiCalled: boolean;
  data: {
    gradesId: string;
  };
}

export interface EllucianAdmissionApplicationRecord {
  applicationId: string;
  academicPeriodId: string;
  applicationAcademicPrograms: { program: { id: string } };
}
